/**
 * String manipulation utilities
 */

/**
 * Calculate Levenshtein distance between two strings
 *
 * The Levenshtein distance is a measure of the difference between two strings,
 * representing the minimum number of single-character edits (insertions, deletions,
 * or substitutions) required to change one string into the other.
 *
 * Commonly used for:
 * - Fuzzy matching and typo correction
 * - "Did you mean...?" suggestions
 * - Spell checking
 * - DNA sequence analysis
 *
 * @param str1 - First string to compare
 * @param str2 - Second string to compare
 * @returns The Levenshtein distance (0 = identical strings)
 *
 * @example
 * ```typescript
 * levenshteinDistance('cat', 'cat')   // 0 (identical)
 * levenshteinDistance('cat', 'cut')   // 1 (one substitution: a→u)
 * levenshteinDistance('cat', 'cast')  // 1 (one insertion: s)
 * levenshteinDistance('cat', 'at')    // 1 (one deletion: c)
 * levenshteinDistance('cat', 'dog')   // 3 (three substitutions)
 * levenshteinDistance('help', 'halp') // 1 (one substitution: e→a)
 * ```
 *
 * Time Complexity: O(m * n) where m and n are string lengths
 * Space Complexity: O(m * n) for the matrix
 */
export function levenshteinDistance(str1: string, str2: string): number {
  const len1 = str1.length
  const len2 = str2.length
  const matrix: number[][] = []

  // Initialize matrix
  // First row: [0, 1, 2, 3, ...] (cost of inserting characters)
  // First column: [0, 1, 2, 3, ...] (cost of deleting characters)
  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i]
  }
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j
  }

  // Calculate distances using dynamic programming
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,       // deletion
        matrix[i][j - 1] + 1,       // insertion
        matrix[i - 1][j - 1] + cost // substitution
      )
    }
  }

  return matrix[len1][len2]
}

/**
 * Check if two strings are similar based on Levenshtein distance
 *
 * @param str1 - First string to compare
 * @param str2 - Second string to compare
 * @param maxDistance - Maximum distance to consider "similar" (default: 3)
 * @returns true if strings are similar (distance <= maxDistance)
 *
 * @example
 * ```typescript
 * isSimilar('help', 'halp')        // true (distance: 1)
 * isSimilar('list', 'ls', 3)       // false (distance: 4)
 * isSimilar('clear', 'claer', 2)   // true (distance: 2)
 * ```
 */
export function isSimilar(str1: string, str2: string, maxDistance: number = 3): boolean {
  return levenshteinDistance(str1, str2) <= maxDistance
}

/**
 * Find the most similar string from a list of candidates
 *
 * @param target - String to match against
 * @param candidates - List of candidate strings
 * @param maxDistance - Maximum distance to consider (default: 3)
 * @returns Array of similar candidates with their distances, sorted by similarity
 *
 * @example
 * ```typescript
 * const commands = ['help', 'list', 'clear', 'exit']
 * findSimilar('halp', commands)
 * // Returns: [{ value: 'help', distance: 1 }]
 *
 * findSimilar('lst', commands)
 * // Returns: [{ value: 'list', distance: 2 }]
 * ```
 */
export function findSimilar(
  target: string,
  candidates: string[],
  maxDistance: number = 3
): Array<{ value: string; distance: number }> {
  const results = candidates
    .map(candidate => ({
      value: candidate,
      distance: levenshteinDistance(target, candidate),
    }))
    .filter(result => result.distance > 0 && result.distance <= maxDistance)
    .sort((a, b) => a.distance - b.distance)

  return results
}

/**
 * Escape special regex characters in a string
 *
 * Escapes all special regex characters so the string can be safely
 * used in a RegExp constructor without interpretation.
 *
 * @param str - String to escape
 * @returns String with regex special characters escaped
 *
 * @example
 * ```typescript
 * escapeRegex('C++')           // 'C\\+\\+'
 * escapeRegex('file.txt')      // 'file\\.txt'
 * escapeRegex('(test)')        // '\\(test\\)'
 * escapeRegex('[a-z]')         // '\\[a-z\\]'
 *
 * // Usage in RegExp:
 * const safe = escapeRegex('C++')
 * const regex = new RegExp(`\\b${safe}\\b`, 'g')
 * ```
 */
export function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
